import { formatDisplayDate } from '../utils/displayDate';
import type {
  AppointmentDetail,
  DocumentType,
  Patient,
  UnreadDocumentCounts,
  WeightHeightDetail,
} from '../types';
import type {
  AppointmentEntry,
  CareTeamMemberEntry,
  DocumentSummaryResponse,
  NotificationPatientRow,
  PatientDetailResponse,
  VitalsEntry,
} from './clinicalTrialsApiTypes';

/**
 * Map a backend document-type string to the frontend `DocumentType` union.
 * The backend stores "PRO ECOG"; the ported UI uses "ECOG".
 *
 * @param backendType - Document type as stored by the service
 * @returns The frontend DocumentType label
 */
export function toFrontendDocType(backendType: string): DocumentType {
  return (backendType === 'PRO ECOG' ? 'ECOG' : backendType) as DocumentType;
}

/**
 * Map a frontend `DocumentType` to the backend document-type string used by
 * the `/api/v1` endpoints ("ECOG" -> "PRO ECOG").
 *
 * @param docType - Frontend DocumentType label
 * @returns The backend document-type string
 */
export function toBackendDocType(docType: DocumentType): string {
  return docType === 'ECOG' ? 'PRO ECOG' : docType;
}

/**
 * Map backend document-type unread counts to frontend category labels.
 *
 * @param counts - Backend document type to unread report count
 * @returns Personalized unread counts keyed by frontend document category
 */
export function toFrontendUnreadCounts(
  counts: Record<string, number>,
): UnreadDocumentCounts {
  return Object.fromEntries(
    Object.entries(counts).map(([type, count]) => [
      toFrontendDocType(type),
      count,
    ]),
  ) as UnreadDocumentCounts;
}

/**
 * Radiology sort priority derived from RECIST disease status: Progressive
 * Disease first, then Stable, then Partial Response, then everything else.
 *
 * @param diseaseStatus - RECIST-derived status (PD/SD/PR/CR) or free text
 * @returns Priority rank (lower = higher priority)
 */
function priorityFromDiseaseStatus(diseaseStatus?: string | null): number {
  const normalized = (diseaseStatus || '').toLowerCase();
  if (normalized.includes('progress') || normalized === 'pd') return 1;
  if (normalized.includes('stable') || normalized === 'sd') return 2;
  if (normalized.includes('partial') || normalized === 'pr') return 3;
  return 4;
}

/**
 * Map a FHIR gender string ("male"/"female"/…) to the UI's `'M' | 'F' | 'Other'`.
 *
 * @param sex - FHIR gender value from the notification row, if any
 * @returns The single-letter gender used by the header components
 */
function sexToGender(sex?: string | null): 'M' | 'F' | 'Other' {
  const normalized = (sex || '').toLowerCase();
  if (normalized === 'male' || normalized === 'm') return 'M';
  if (normalized === 'female' || normalized === 'f') return 'F';
  return 'Other';
}

/**
 * Convert a Notification View row from the API into the rich `Patient` shape the
 * ported ct-tech-handoff views expect. Fields the V1 API does not provide are
 * filled with safe defaults so the table and summary views render cleanly.
 *
 * @param row - One notification row from `daily_notifications/{date}`
 * @returns A `Patient` object for the notification table
 */
export function rowToPatient(row: NotificationPatientRow): Patient {
  const documentTypes = (row.document_types || []).map(toFrontendDocType);
  // Landing table shows the 'C'-type ICD-10 codes from the FHIR Conditions API
  // (comma-separated when multiple). The single MD-note extraction code is shown only
  // in the patient DETAIL view (see mergeDetailIntoPatient). Fall back to the MD-note
  // code when a patient has no cancer conditions.
  const conditionCodes = row.condition_cancer_codes || [];
  // dxCode stays a single code (drives the cancer-scale/sort logic); the landing table
  // renders each of `diagnosisCodes` as its own badge. Detail view overrides dxCode with
  // the MD-note code (see mergeDetailIntoPatient).
  const dxCode = conditionCodes[0] || row.diagnosis_code || '';
  const dxDescription = row.diagnosis || '';

  return {
    id: row.mrn,
    mrn: row.mrn,
    name: row.patient_name || row.mrn,
    age: row.age ?? 0,
    gender: sexToGender(row.sex),
    dateOfBirth: '',

    diagnoses: dxCode
      ? [{ code: dxCode, description: dxDescription, isPrimary: true }]
      : [],
    diagnosis: dxDescription,
    diagnosisShort: dxDescription,
    dxCode,
    diagnosisCodes: conditionCodes,
    isCancer: !!row.is_cancer,
    patient_fhir_identifier: row.patient_fhir_identifier || undefined,
    stage: row.stage || '',
    diagnosisDate: '',

    careTeam: [],
    principalMD: '',
    allergies: [],

    lastVisit: '',
    nextAppointment: row.next_appointment || null,

    patientStatus: row.patient_status || '',
    priority: priorityFromDiseaseStatus(row.disease_status),
    diseaseStatus: row.disease_status || undefined,
    ecogScore: row.ecog_score ?? null,

    location: '',
    insurance: row.insurance || undefined,
    insuranceCategory: row.insurance_category || undefined,

    documentName: '',
    documentNamesByType: row.document_names || {},
    documentTypes,
    latestDocsByType: Object.fromEntries(
      Object.entries(row.latest_docs_by_type || {}).map(([type, docs]) => [
        type,
        (docs || []).map((d) => ({
          documentId: d.document_id,
          documentName: d.document_name || undefined,
          signoffDetails: d.signoff_details || undefined,
          creationDate: d.creation_date || undefined,
          isRead: d.is_read,
        })),
      ]),
    ),
    mdCancerCodes: row.md_cancer_codes || [],
    conditionCancers: (row.condition_cancers || []).map((c) => ({
      code: c.code,
      description: c.description || '',
      stage: c.stage || '',
      diagnosedOn: c.diagnosed_on || '',
    })),

    // V1 simplifications: trials/watchlist/notes are deferred to V2.
    trials: row.trials || [],
    alerts: row.alerts && row.alerts.length ? row.alerts : '',
    tags: [],
    patientTags: row.patient_tags || [],
    notes: [],
    isWatchlisted: row.is_watchlisted ?? false,

    // Document ids per type let the summary view resolve a PDF for "View Source".
    // Stored on a side channel so the strict Patient type stays intact.
    _documentIds: row.document_ids || [],
  } as Patient & { _documentIds: string[]; ecogScore: number | null };
}

/**
 * Expand patients into per-document rows for the active tab. A patient with N latest-date
 * documents of that type yields N rows (each carrying its document id / name / creation
 * date). Every document-backed row receives a stable document-specific React id.
 *
 * @param patients - The tab's patients
 * @param tab - The active document-type tab
 * @returns One row per latest-date document of that type
 */
export function expandRowsForTab(
  patients: Patient[],
  tab: DocumentType,
): Patient[] {
  const backendType = toBackendDocType(tab);
  const rows: Patient[] = [];
  for (const patient of patients) {
    const docs = patient.latestDocsByType?.[backendType] || [];
    if (!docs.length) {
      rows.push(patient);
      continue;
    }
    for (const doc of docs) {
      rows.push({
        ...patient,
        id: `${patient.mrn}__${doc.documentId}`,
        documentId: doc.documentId,
        documentName: doc.documentName || patient.documentName,
        signoffDetails: doc.signoffDetails,
        documentCreationDate: doc.creationDate,
        isRead: doc.isRead,
      });
    }
  }
  return rows;
}

/**
 * Merge a document summary response onto a patient object. The API's
 * `structured_summary` uses the same field names the rich summary components
 * read (e.g. `mdVisitNote`, `pathologyReport`, `laboratoryResults`), so spreading
 * it hydrates the DocumentSummaryView; `alert_text` populates `alerts`.
 *
 * @param patient - The base patient (from the notification row)
 * @param summary - The document summary response for the active doc type
 * @returns A new patient enriched with the structured summary
 */
export function mergeSummaryIntoPatient(
  patient: Patient,
  summary: DocumentSummaryResponse,
): Patient {
  const structured = (summary.structured_summary || {}) as Record<
    string,
    unknown
  >;
  const merged = {
    ...patient,
    ...(structured as Partial<Patient>),
    alerts: summary.alert_text || patient.alerts,
  };
  // Per-document rows: when this row is a specific document and the summary carries that
  // document's own shaped block, render IT on top (radiology/mdVisitNote/…) while the
  // narrative + comparative stay shared. Multiple same-date reports thus differ per row.
  const perDoc =
    (structured.documentsById as
      | Record<string, Record<string, unknown>>
      | undefined) || {};
  if (patient.documentId && perDoc[patient.documentId]) {
    const perDocBlock = { ...perDoc[patient.documentId] };
    // ECOG is ONE shared trend across every assessment (never per-document): the per-doc
    // block carries only the clicked assessment (history length 1), so applying it would
    // collapse the trend chart to a single point. Drop it and keep the shared trend.
    delete perDocBlock.ecog;
    Object.assign(merged, perDocBlock);
  }
  // The patient + diagnosis bands are patient-level (sourced from the latest MD note via the
  // patient detail) and must render IDENTICALLY across every document-type view. A per-doc
  // summary must never change them, so re-assert the band fields from the base patient.
  return {
    ...merged,
    diagnosis: patient.diagnosis,
    diagnosisShort: patient.diagnosisShort,
    dxCode: patient.dxCode,
    isCancer: patient.isCancer,
    stage: patient.stage,
    histology: patient.histology,
    tnmClassification: patient.tnmClassification,
    metastaticSites: patient.metastaticSites,
    diseaseStatus: patient.diseaseStatus,
    lineOfTherapy: patient.lineOfTherapy,
    ecogStatus: patient.ecogStatus,
    diagnosisDate: patient.diagnosisDate,
  };
}

/**
 * Join weight / height / BSA into the grid's "Wt/Ht/BSA" line.
 *
 * @param vitals - Latest vitals entry from the patient detail, if any
 * @returns The combined string, or undefined when no measurements exist
 */
function vitalsToString(vitals?: VitalsEntry | null): string | undefined {
  if (!vitals) return undefined;
  const parts = [vitals.weight, vitals.height, vitals.bsa].filter(Boolean);
  return parts.length ? parts.join(' / ') : undefined;
}

/**
 * Map a backend vitals entry to the grid's `WeightHeightDetail` tooltip shape.
 *
 * @param vitals - Latest vitals entry, if any
 * @returns The detail object, or undefined when no vitals exist
 */
function vitalsToDetails(
  vitals?: VitalsEntry | null,
): WeightHeightDetail | undefined {
  if (!vitals) return undefined;
  return {
    weight: vitals.weight || '',
    height: vitals.height || '',
    bsa: vitals.bsa || '',
    dateRecorded: formatDisplayDate(vitals.recorded_date),
  };
}

/**
 * Map a backend appointment entry to the UI's `AppointmentDetail` card.
 *
 * @param appt - Appointment entry (next visit / next MD visit), if any
 * @returns The AppointmentDetail, or undefined when absent
 */
function apptToDetail(
  appt?: AppointmentEntry | null,
): AppointmentDetail | undefined {
  if (!appt) return undefined;
  return {
    location: appt.location || '',
    provider: appt.provider || undefined,
    type: appt.description || undefined,
  };
}

/**
 * Parse a line-of-therapy label ("3", "3L", "Line 3") into an integer.
 *
 * @param value - Backend line-of-therapy label, if any
 * @returns The parsed line number, or undefined when not numeric
 */
function parseLineOfTherapy(value?: string | null): number | undefined {
  if (!value) return undefined;
  const match = value.match(/\d+/);
  return match ? Number(match[0]) : undefined;
}

/**
 * Pick the principal MD (Physician of Record) from a care team, falling back
 * to the first member.
 *
 * @param careTeam - Care-team entries from the patient detail
 * @returns The principal MD display name, or "" when the team is empty
 */
function principalMdFrom(careTeam: CareTeamMemberEntry[]): string {
  const record = careTeam.find((m) =>
    (m.role || '').toLowerCase().includes('physician of record'),
  );
  return record?.name || careTeam[0]?.name || '';
}

/**
 * Flatten the patient API's structured metastatic-site assessment for legacy
 * document-summary components that consume a string list.
 *
 * @param sites - Flat legacy list or structured confirmed/suspected assessment
 * @returns Displayable metastatic site names
 */
function flattenMetastaticSites(
  sites: PatientDetailResponse["metastatic_sites"],
): string[] {
  if (Array.isArray(sites)) return sites;
  return [...(sites.confirmed ?? []), ...(sites.suspected ?? [])];
}

/**
 * Merge the enriched patient-detail record onto a base patient. The detail
 * supplies the rich header fields (allergies, care team, vitals, appointments,
 * insurance, contact/preferences) and the diagnosis-section fields (histology,
 * TNM, metastatic sites, line of therapy, ECOG). Scalar fields the notification
 * row already provides (friendly diagnosis, stage, disease status) are kept when
 * present and only backfilled from the detail.
 *
 * @param base - The base patient (from the notification row + summary)
 * @param detail - The enriched patient-detail response
 * @returns A new patient enriched for the Document Summary header
 */
export function mergeDetailIntoPatient(
  base: Patient,
  detail: PatientDetailResponse,
): Patient {
  const demo = detail.demographics || {};
  const careTeam = (detail.care_team || []).map((m) => ({
    name: m.name || '',
    role: m.role || '',
  }));
  const allergies = (detail.allergies || []).map((a) => ({
    substance: a.substance || '',
    type: a.type || '',
    severity: a.severity || undefined,
  }));
  const diagnoses = (detail.diagnoses || []).map((d) => ({
    code: d.code || '',
    description: d.description || '',
    isPrimary: d.is_primary,
  }));
  const cancers = (detail.cancers || []).map((c) => ({
    code: c.code || '',
    description: c.description || '',
    stage: c.stage || '',
    diagnosedOn: c.diagnosed_on || '',
    histology: c.histology || '',
  }));
  const nextVisitDetail = apptToDetail(detail.next_visit);
  const metastaticSites = flattenMetastaticSites(detail.metastatic_sites);

  return {
    ...base,
    cancers,

    // Identity banner
    name: detail.patient_name || base.name,
    age: typeof demo.age === 'number' ? demo.age : base.age,
    gender: demo.sex ? sexToGender(String(demo.sex)) : base.gender,
    dateOfBirth:
      formatDisplayDate(
        typeof demo.birth_date === 'string' ? demo.birth_date : '',
      ) || base.dateOfBirth,
    pronouns:
      (typeof demo.pronouns === 'string' ? demo.pronouns : undefined) ||
      base.pronouns,
    patient_fhir_identifier:
      detail.patient_fhir_identifier || base.patient_fhir_identifier,

    // Diagnosis section — code + description are authoritative from the detail
    // response (shown for all patients); isCancer drives the rich band + summary.
    diagnosis: detail.diagnosis || base.diagnosis,
    diagnosisShort: detail.diagnosis || base.diagnosisShort,
    dxCode: detail.diagnosis_code || base.dxCode,
    isCancer: detail.is_cancer ?? base.isCancer,
    diagnoses: diagnoses.length ? diagnoses : base.diagnoses,
    stage: base.stage || detail.stage || '',
    diagnosisDate:
      formatDisplayDate(detail.diagnosis_date) || base.diagnosisDate,
    histology: detail.histology || base.histology,
    tnmClassification: detail.tnm_classification || base.tnmClassification,
    metastaticSites: metastaticSites.length
      ? metastaticSites
      : base.metastaticSites,
    lineOfTherapy:
      parseLineOfTherapy(detail.line_of_therapy) ?? base.lineOfTherapy,
    ecogStatus: detail.ecog_score ?? base.ecogStatus,
    diseaseStatus: base.diseaseStatus || detail.disease_status || undefined,

    // Care team + allergies
    careTeam: careTeam.length ? careTeam : base.careTeam,
    principalMD: careTeam.length
      ? principalMdFrom(detail.care_team)
      : base.principalMD,
    allergies: allergies.length ? allergies : base.allergies,

    // Contact + preferences
    preferredPhone:
      (typeof demo.phone === 'string' ? demo.phone : undefined) ||
      base.preferredPhone,
    preferredLanguage:
      (typeof demo.language === 'string' ? demo.language : undefined) ||
      base.preferredLanguage,
    preferredLocation: detail.preferred_location || base.preferredLocation,

    // Visits ("Next visit" = any appointment; "Next MD visit" = MD-only)
    nextVisit: detail.next_visit?.start || base.nextVisit,
    nextVisitDetails: nextVisitDetail
      ? [nextVisitDetail]
      : base.nextVisitDetails,
    nextAppointment: detail.next_md_visit?.start || base.nextAppointment,
    nextMDVisitDetails:
      apptToDetail(detail.next_md_visit) || base.nextMDVisitDetails,

    // Orders
    upcomingOrder: detail.upcoming_order || base.upcomingOrder,
    upcomingOrderDate: detail.upcoming_order_date || base.upcomingOrderDate,

    // Vitals
    weightHeight: vitalsToString(detail.vitals) || base.weightHeight,
    weightHeightDetails:
      vitalsToDetails(detail.vitals) || base.weightHeightDetails,

    // Insurance + status
    insurance: detail.insurance || detail.insurance_category || base.insurance,
    insuranceCategory: detail.insurance_category || base.insuranceCategory,
    patientStatus: base.patientStatus || detail.patient_status || '',

    // Tags — the detail response is the source of truth for the persisted CRC tags.
    patientTags: detail.patient_tags?.length
      ? detail.patient_tags
      : base.patientTags,
  };
}
